/**
 * Provider commission (percent to pay) — default on user + optional per-service overrides.
 */

/**
 * @param {number|string|null|undefined} value
 * @param {number} [fallback=100]
 * @returns {number}
 */
export function normalizePercentToPay(value, fallback = 100) {
  const raw = Number(value);
  if (!Number.isFinite(raw)) {
    return Math.max(0, Math.min(100, Math.round(Number(fallback) || 100)));
  }
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/**
 * @param {{ assignedServices?: Array<{ serviceId: string, percentToPay?: number }>, serviceIds?: string[] }|null|undefined} user
 * @returns {Array<{ serviceId: string, percentToPay?: number }>}
 */
export function getAssignedServices(user) {
  if (user && Array.isArray(user.assignedServices) && user.assignedServices.length > 0) {
    return user.assignedServices
      .filter((entry) => entry && entry.serviceId != null && entry.serviceId !== '')
      .map((entry) => ({
        serviceId: String(entry.serviceId),
        ...(entry.percentToPay != null && entry.percentToPay !== ''
          ? { percentToPay: normalizePercentToPay(entry.percentToPay) }
          : {}),
      }));
  }
  const ids = user && Array.isArray(user.serviceIds) ? user.serviceIds : [];
  return ids.filter(Boolean).map((serviceId) => ({ serviceId: String(serviceId) }));
}

/**
 * @param {{ percentToPay?: number, assignedServices?: Array<{ serviceId: string, percentToPay?: number }>, serviceIds?: string[] }|null|undefined} user
 * @param {string} serviceId
 * @returns {number}
 */
export function resolvePercentToPay(user, serviceId) {
  const defaultPct = normalizePercentToPay(user?.percentToPay, 100);
  if (serviceId == null || serviceId === '') return defaultPct;
  const sid = String(serviceId);
  const assigned = getAssignedServices(user);
  const entry = assigned.find((a) => a.serviceId === sid);
  if (entry && entry.percentToPay != null) {
    return normalizePercentToPay(entry.percentToPay, defaultPct);
  }
  return defaultPct;
}

/**
 * @param {number} amount
 * @returns {number}
 */
function roundMoney(amount) {
  return Math.round((Number(amount) || 0) * 100) / 100;
}

/**
 * @param {number} timestamp
 * @returns {string} YYYY-MM-DD in local timezone
 */
export function appointmentDateKey(timestamp) {
  const d = new Date(Number(timestamp));
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Sum provider pay by lash from appointment line expected prices.
 * @param {Record<string, { lash?: string, date?: number, services?: Array<{ service?: string, price?: number }> }>} appointmentsById
 * @param {Record<string, object>} users
 * @param {string} minDateKeyInclusive - YYYY-MM-DD (same as reportDaily buckets)
 * @param {string} maxDateKeyInclusive - YYYY-MM-DD
 * @returns {Record<string, number>}
 */
export function computePayToProviderByLash(
  appointmentsById,
  users,
  minDateKeyInclusive,
  maxDateKeyInclusive
) {
  const payByLash = {};
  const minKey = String(minDateKeyInclusive || '');
  const maxKey = String(maxDateKeyInclusive || '');
  if (!minKey || !maxKey) return payByLash;

  for (const appt of Object.values(appointmentsById || {})) {
    if (!appt || appt.lash == null || appt.lash === '') continue;
    const dateKey = appointmentDateKey(appt.date);
    if (!dateKey || dateKey < minKey || dateKey > maxKey) continue;

    const lashId = appt.lash;
    const user = users?.[lashId];
    const lines = Array.isArray(appt.services) ? appt.services : [];
    let appointmentPay = 0;
    for (const line of lines) {
      const serviceId = line?.service;
      if (serviceId == null || serviceId === '') continue;
      const price = Number(line.price) || 0;
      const pct = resolvePercentToPay(user, serviceId);
      appointmentPay += price * (pct / 100);
    }
    payByLash[lashId] = roundMoney((payByLash[lashId] || 0) + appointmentPay);
  }

  return payByLash;
}

/**
 * @param {string|number} value
 * @returns {boolean}
 */
export function isValidProviderPercentInput(value) {
  if (value === '' || value == null) return true;
  const raw = Number(value);
  if (!Number.isFinite(raw)) return false;
  if (!Number.isInteger(raw)) return false;
  return raw >= 0 && raw <= 100;
}

/**
 * Build assignedServices payload and derived serviceIds for Firestore.
 * @param {string[]} selectedServiceIds
 * @param {Record<string, string|number>} overridesByServiceId - empty string = use default
 * @param {number} defaultPercentToPay
 * @returns {{ assignedServices: Array<{ serviceId: string, percentToPay?: number }>, serviceIds: string[] }}
 */
export function buildAssignedServicesPayload(selectedServiceIds, overridesByServiceId, defaultPercentToPay) {
  const defaultPct = normalizePercentToPay(defaultPercentToPay, 100);
  const serviceIds = selectedServiceIds.filter(Boolean).map(String);
  const assignedServices = serviceIds.map((serviceId) => {
    const entry = { serviceId };
    const raw = overridesByServiceId?.[serviceId];
    if (raw === '' || raw == null) return entry;
    if (!isValidProviderPercentInput(raw)) return entry;
    const pct = normalizePercentToPay(raw, defaultPct);
    if (pct !== defaultPct) entry.percentToPay = pct;
    return entry;
  });
  return { assignedServices, serviceIds };
}

/**
 * @param {{ assignedServices?: Array<{ serviceId: string, percentToPay?: number }>, serviceIds?: string[] }|null|undefined} user
 * @returns {Record<string, number>}
 */
export function assignedServiceOverridesFromUser(user) {
  const out = {};
  for (const entry of getAssignedServices(user)) {
    if (entry.percentToPay != null) {
      out[entry.serviceId] = entry.percentToPay;
    }
  }
  return out;
}
